/**
 * analytics-shared/api/client.js
 *
 * Two axios instances for the reports + dashboards stack:
 *
 *   analyticsClient — rooted at ${BACKEND_URL}/api/v1. Used by the future
 *     /reports/* and /dashboards/* endpoints. Today most of these still
 *     fall through to localStorage; the client is wired up so the swap is a
 *     one-line change inside each api module.
 *
 *   crmClient — rooted at ${BACKEND_URL}/api. Used to call the existing
 *     non-versioned CRM endpoints (e.g. /api/data-operations/metadata/...).
 *     The report builder uses this for live object & field discovery.
 *
 * Both share the same auth header interceptor and the same normalised error
 * shape so consumers can branch on .status without sniffing axios internals.
 */
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const V1 = `${BACKEND}/api/v1`;
const API = `${BACKEND}/api`;

function attachInterceptors(instance) {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    (err) => {
      const status = err.response?.status;
      const data = err.response?.data;
      // FastAPI 422s ship detail as an array of {loc, msg} objects —
      // flatten to readable text so toasts never show "[object Object]".
      let detail = data?.message || data?.detail || err.message || 'Request failed';
      if (Array.isArray(detail)) {
        detail = detail.map((d) => (d && typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d))).join('; ');
      } else if (detail && typeof detail === 'object') {
        detail = detail.message || detail.msg || JSON.stringify(detail);
      }
      return Promise.reject({ status, message: String(detail), data, original: err });
    },
  );
  return instance;
}

export const analyticsClient = attachInterceptors(axios.create({
  baseURL: V1,
  timeout: 20000,
}));

export const crmClient = attachInterceptors(axios.create({
  baseURL: API,
  timeout: 20000,
}));

export const unwrap = (res) => res.data;
